import { useTranslation } from 'react-i18next'
import { FileText, Images } from 'lucide-react'
import { EID } from '@/lib/element-ids.registry'
import { formatDateTime } from '@/lib/utils'
import type { ChangeEvent } from '@/lib/api/performance'

interface Props {
  events: ChangeEvent[]
  channelName: (id: string | null) => string
  onEventClick: (ev: ChangeEvent) => void
}

export function PerformanceEventList({ events, channelName, onEventClick }: Props) {
  const { t } = useTranslation()
  if (events.length === 0) {
    return <p className="text-sm text-neutral-500 py-3 text-center">{t('performance.empty.events')}</p>
  }
  const sorted = [...events].sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
  return (
    <div id={EID['performance-event-list']}>
      <h6 className="text-sm font-semibold text-neutral-700 dark:text-neutral-300 mb-2">
        {t('performance.eventList.title')}
      </h6>
      <ul className="space-y-1.5">
        {sorted.map((ev) => {
          const Icon = ev.event_type === 'image' ? Images : FileText
          const title = ev.event_type === 'image'
            ? t('performance.events.image', { slot: ev.slot ?? '?' })
            : t(`performance.blockType.${ev.block_type}`, ev.block_type ?? '')
          return (
            <li key={ev.id}>
              <button type="button" id={`${EID['performance-event-item']}-${ev.id}`}
                onClick={() => onEventClick(ev)}
                className="w-full flex items-center gap-2 text-left text-sm rounded-md border border-neutral-200 dark:border-neutral-800 px-2.5 py-1.5 hover:border-primary-500">
                <Icon className="h-4 w-4 text-amber-500 flex-shrink-0" aria-hidden="true" />
                <span className="font-medium text-neutral-800 dark:text-neutral-200">{title}</span>
                <span className="text-neutral-400">· {channelName(ev.channel_id)}</span>
                <span className="ml-auto text-neutral-400">{formatDateTime(ev.timestamp)}</span>
              </button>
            </li>
          )
        })}
      </ul>
    </div>
  )
}
